import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Ticket, CheckCircle, AlertCircle, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('No verification token provided');
        setVerifying(false);
        return;
      }
      
      try {
        await axios.get(`${API_URL}/auth/verify-email?token=${token}`);
        setSuccess(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Verification failed');
      } finally {
        setVerifying(false);
      }
    };
    
    verifyEmail();
  }, [token]);

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying your email...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <Ticket className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl">FanBound</span>
          </Link>
        </div>

        <Card className="bg-card border-white/10">
          <CardContent className="pt-6">
            {success ? (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-2xl mb-2">Email Verified!</h2>
                  <p className="text-muted-foreground">
                    Your email has been successfully verified. You now have full access to all FanBound features.
                  </p>
                </div>
                <div className="space-y-3">
                  <Button className="w-full" onClick={() => navigate('/events')}>
                    Browse Events
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/dashboard')}>
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                  <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-2xl mb-2">Verification Failed</h2>
                  <p className="text-muted-foreground">
                    {error || 'This verification link is invalid or has expired.'}
                  </p>
                </div>
                <div className="space-y-3">
                  <Button className="w-full gap-2" onClick={() => navigate('/login')}>
                    <Mail className="w-4 h-4" />
                    Login to Resend
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Log in to your account to request a new verification email.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
